/**
 * The agent editor's parameter-lifecycle notices.
 *
 * These are deliberately PRESENTATIONAL and prop-driven — every one of them
 * renders text the server authored or a count the editor computed from the
 * server's resolved schema. None of them evaluates a policy predicate, and none
 * composes a constraint of its own. That is the same structural property
 * `ParamControl`'s `inertReason` has, and for the same reason: a client that
 * cannot state a rule cannot state a rule the backend does not enforce.
 *
 * Being prop-driven also puts them squarely in the Storybook tier per the
 * CLAUDE.md rubric — see `__stories__/AgentEditorInheritOverride.stories.tsx`,
 * `AgentEditorModelChange.stories.tsx` and `AgentEditorDeprecation.stories.tsx`.
 */
import type { ModelLifecycleStatus } from '@localagent/sdk';

/**
 * A params-carrying write rejected with 409 `catalog_schema_stale`.
 *
 * The distinction that matters: this is NOT a param-level error. The values the
 * user typed may every one of them be legal; what changed is the SCHEMA they
 * were legal against. Rendering a per-param message here would send the user
 * hunting for a bad number that does not exist, so the only correct affordance
 * is "reload and look again".
 */
export function SchemaStaleNotice({ onReload }: { onReload: () => void }) {
  return (
    <div
      data-testid="agent-schema-stale-notice"
      className="flex items-center justify-between gap-3 rounded-md bg-warning-subtle p-3 text-label"
    >
      <span>
        The model&apos;s parameters changed since you opened this editor — reload to get the
        current controls, then save again.
      </span>
      <button
        type="button"
        data-testid="agent-schema-stale-reload"
        onClick={onReload}
        className="shrink-0 rounded-md bg-secondary px-2 py-1 text-caption font-medium text-secondary-foreground hover:bg-secondary/80"
      >
        Reload
      </button>
    </div>
  );
}

/**
 * The counted, NON-BLOCKING override notice.
 *
 * The count is the whole point. Under sparse pins, an unpinned param picks up a
 * corrected default on the agent's next turn; a pinned one never does. So "how
 * many of this agent's parameters have opted out of that" is the single number
 * that explains why a platform-wide correction did or did not reach this agent —
 * and it is invisible without saying it out loud.
 */
export function ParamOverrideNotice({
  count,
  onResetAll,
  disabled,
}: {
  count: number;
  onResetAll: () => void;
  disabled: boolean;
}) {
  return (
    <div
      data-testid="agent-params-override-notice"
      data-override-count={String(count)}
      className="flex items-center justify-between gap-3 rounded-md bg-muted p-3 text-caption"
    >
      <span>
        <strong>
          {count} parameter{count === 1 ? '' : 's'} overridden
        </strong>{' '}
        — overridden parameters keep the value you set and will not pick up a change to this
        model&apos;s defaults. Everything else follows the defaults automatically.
      </span>
      <button
        type="button"
        data-testid="agent-params-reset-all"
        onClick={onResetAll}
        disabled={disabled}
        className="shrink-0 rounded-md bg-background px-2 py-1 font-medium hover:bg-background/80 disabled:opacity-50"
      >
        Reset all to defaults
      </button>
    </div>
  );
}

/**
 * The pins a model change DROPPED.
 *
 * `pruneOverridesToSpec` has always dropped params the new model does not
 * define; it had to, because a param with no control cannot be seen, edited or
 * cleared, and it hard-fails the backend's closed-world validator on every turn.
 * What was missing is the sentence. Losing a value the user explicitly set,
 * silently, is the same failure class this feature exists to remove — so the
 * drop is reported rather than merely performed.
 */
export function DroppedOverridesNotice({ dropped }: { dropped: string[] }) {
  return (
    <div
      data-testid="agent-model-change-dropped-overrides"
      data-dropped-count={String(dropped.length)}
      className="rounded-md bg-warning-subtle p-3 text-caption"
    >
      The new model does not support {dropped.length === 1 ? 'a parameter' : 'parameters'} you
      had overridden, so {dropped.length === 1 ? 'it was' : 'they were'} removed:{' '}
      <strong>{dropped.join(', ')}</strong>.
    </div>
  );
}

/**
 * A dated lifecycle warning for a deprecated or retired model,
 * with an optional deep-link into the existing Providers reassign flow.
 *
 * NOTHING BLOCKS. Both states render, neither prevents a save, and no
 * control is disabled: the user may have a deliberate reason to stay on the
 * model, and a product that silently refuses the save teaches them nothing while
 * costing them the edit they came to make.
 *
 * `status` is DERIVED SERVER-SIDE from `deprecation_date` and rendered verbatim —
 * the client runs no date comparison, so it cannot disagree with the backend
 * about whether a model is past its date.
 *
 * "Find a replacement" is a navigation affordance into the same bulk
 * reassign path behind `DeleteEntryDialog` — it does not gate the save.
 */
export function ModelDeprecationWarning({
  modelName,
  status,
  deprecationDate,
  onFindReplacement,
}: {
  modelName: string;
  status: ModelLifecycleStatus;
  /** Absent when the server sent none — the warning is shown undated rather than invented. */
  deprecationDate?: string;
  /** Open the Providers reassign flow with this model in focus. */
  onFindReplacement?: () => void;
}) {
  if (status === 'active') return null;
  const retired = status === 'retired';
  return (
    <div
      data-testid="agent-model-deprecation-warning"
      data-model-status={status}
      className={`flex items-start justify-between gap-3 rounded-md p-3 text-caption ${
        retired
          ? 'bg-error-subtle text-error-text'
          : 'bg-warning-subtle text-warning-text'
      }`}
    >
      <div>
        <strong>{modelName}</strong> is {retired ? 'retired' : 'deprecated'}
        {deprecationDate ? (
          <>
            {' '}
            as of{' '}
            <time data-testid="agent-model-deprecation-date" dateTime={deprecationDate}>
              {deprecationDate}
            </time>
          </>
        ) : null}
        .{' '}
        {retired
          ? 'The provider may reject requests for it. Pick a current model when you can.'
          : 'It still works today, and will stop working once the provider retires it.'}{' '}
        You can still save this agent.
      </div>
      {onFindReplacement && (
        <button
          type="button"
          data-testid="agent-model-find-replacement"
          onClick={onFindReplacement}
          className="shrink-0 rounded-md bg-secondary px-2 py-1 text-caption font-medium text-secondary-foreground hover:bg-secondary/80"
        >
          Find a replacement
        </button>
      )}
    </div>
  );
}
